import { Loader } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

import classes from "./HoverVideo.module.css";

/** Live's `LOADER_APPEAR_DELAY_MS`: a clip that starts sooner shows no loader. */
export const LOADER_APPEAR_DELAY_MS = 300;

export interface HoverVideoProps {
  /** The silent clip that plays while hovered; none keeps the poster alone. */
  src: string | null;
  poster: string | null;
  /** The hover is the caller's: the card around it tracks the mouse. */
  hovering: boolean;
}

/**
 * Live's `HoverVideo` (its catalog cards: Hooks' hooks, Hero Shot's styles), with what it
 * fetches cut to a hover: the poster underneath, the clip's video on top of it, muted, loaded
 * only when hovered (`preload="none"`, never reloaded on mount, so nine cards are nine posters,
 * not live's burst of nine clips). Hovering plays it from its whole file (`preload` set to `auto`), with live's
 * loader (20 px, "Loading video") once it has waited 300 ms without playing; leaving pauses it on
 * its first frame, which the poster is (the card's first-frame poster, Hooks' notes).
 */
export function HoverVideo({ src, poster, hovering }: HoverVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!hovering || node === null) return;
    node.preload = "auto";
    // Refused only where playback is not allowed: the card stays as it is.
    node.play().catch(() => undefined);
    const timer = setTimeout(() => setWaited(true), LOADER_APPEAR_DELAY_MS);
    return () => {
      clearTimeout(timer);
      node.pause();
      // Back to its first frame, the poster's, with no reload and no request.
      if (node.readyState > 0) node.currentTime = 0;
      setPlaying(false);
      setWaited(false);
    };
  }, [hovering]);
  return (
    <span className={classes.media}>
      {poster === null ? null : <img src={poster} alt="" draggable={false} className={classes.poster} />}
      {src === null ? null : (
        // A preview has no words to caption.
        // oxlint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={ref}
          src={src}
          loop
          playsInline
          muted
          preload="none"
          className={classes.video}
          aria-hidden
          onPlaying={() => setPlaying(true)}
        />
      )}
      {hovering && waited && !playing ? (
        <Loader size={20} aria-label="Loading video" className={classes.loader} />
      ) : null}
    </span>
  );
}
